import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, X, CheckCircle2 } from 'lucide-react';
import type { ReadinessItem } from '@/lib/unitReadiness';

interface ReadinessNudgeData {
  id: string;
  unitId: string;
  unitName: string;
  isReady: boolean;
  missingRecommended: ReadinessItem[];
  /** Storage key for "user already dismissed this for this unit". */
  dismissKey: string;
}

type Listener = (toast: ReadinessNudgeData) => void;
let nudgeListener: Listener | null = null;

const DISMISS_PREFIX = 'bookiapp:readiness-nudge-shown:';

/**
 * Show the proactive "Spreman si za rad" nudge.
 * No-ops if the user already saw and dismissed this for this unit.
 */
export function showReadinessNudge(input: Omit<ReadinessNudgeData, 'id' | 'dismissKey'>): void {
  const dismissKey = `${DISMISS_PREFIX}${input.unitId}`;
  try {
    if (typeof window !== 'undefined' && window.localStorage.getItem(dismissKey)) {
      return; // already dismissed for this unit
    }
  } catch {
    // ignore
  }
  const id = crypto.randomUUID();
  nudgeListener?.({ ...input, id, dismissKey });
}

/** Reset the "already seen" flag — used by tests. */
export function _resetReadinessNudge(unitId: string): void {
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(`${DISMISS_PREFIX}${unitId}`);
    }
  } catch {
    // ignore
  }
}

/**
 * Mount once near the app root. Listens for showReadinessNudge() calls
 * and renders a dismissable bottom-right card with the AI assistant tone.
 */
export function ReadinessNudgeContainer() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState<ReadinessNudgeData | null>(null);
  const dismissedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    nudgeListener = (data) => {
      // Extra in-memory guard so rapid re-renders can't re-show after dismiss.
      if (dismissedRef.current.has(data.unitId)) return;
      setCurrent(data);
    };
    return () => {
      nudgeListener = null;
    };
  }, []);

  const dismiss = useCallback(() => {
    if (!current) return;
    dismissedRef.current.add(current.unitId);
    try {
      window.localStorage.setItem(current.dismissKey, String(Date.now()));
    } catch {
      // ignore
    }
    setCurrent(null);
  }, [current]);

  if (!current) return null;

  const recommendedCount = current.missingRecommended.length;

  return (
    <div
      className="fixed bottom-6 right-6 z-[60] w-[min(380px,calc(100vw-2rem))] animate-in slide-in-from-bottom-4 fade-in duration-300"
      role="dialog"
      aria-live="polite"
    >
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-[hsl(var(--border))] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-4 text-white relative">
          <button
            type="button"
            onClick={dismiss}
            className="absolute top-3 right-3 p-1 rounded-lg hover:bg-white/20 transition-colors"
            aria-label={t('common.close', 'Zatvori')}
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 pr-7">
            <CheckCircle2 className="w-6 h-6 flex-shrink-0" />
            <h3 className="font-semibold">
              {t('readiness.nudge.title', 'Spreman si za rad!')}
            </h3>
          </div>
          <p className="text-sm text-white/90 mt-1">
            {t('readiness.nudge.unitReady', '"{{name}}" ima sve obavezno popunjeno.', {
              name: current.unitName,
            })}
          </p>
        </div>

        {/* Body */}
        <div className="p-4">
          {recommendedCount > 0 ? (
            <>
              <p className="text-sm text-[hsl(var(--foreground))]">
                {t(
                  'readiness.nudge.couldImprove',
                  'Dobro bi bilo dodati još par stvari da gost ima bolje iskustvo:'
                )}
              </p>
              <ul className="mt-2 space-y-1">
                {current.missingRecommended.slice(0, 4).map((item) => (
                  <li
                    key={item.id}
                    className="text-sm text-[hsl(var(--muted-foreground))] flex items-center gap-2"
                  >
                    <Sparkles className="w-3 h-3 text-amber-500 flex-shrink-0" />
                    {t(`readiness.fields.${item.id}`, item.id)}
                  </li>
                ))}
                {recommendedCount > 4 && (
                  <li className="text-xs text-[hsl(var(--muted-foreground))] italic pl-5">
                    {t('readiness.nudge.andMore', '…i još {{n}}', {
                      n: recommendedCount - 4,
                    })}
                  </li>
                )}
              </ul>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-3 italic">
                {t(
                  'readiness.nudge.optional',
                  'Ako ne želiš, slobodno možeš krenuti — sve je spremno.'
                )}
              </p>
            </>
          ) : (
            <p className="text-sm text-[hsl(var(--foreground))]">
              {t(
                'readiness.nudge.allDone',
                'Sve je popunjeno do detalja — gosti će biti oduševljeni 🎉'
              )}
            </p>
          )}

          <button
            type="button"
            onClick={dismiss}
            className="w-full mt-4 px-3 py-2 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-semibold hover:opacity-90 transition-opacity"
          >
            {t('readiness.nudge.gotIt', 'U redu, hvala')}
          </button>
        </div>
      </div>
    </div>
  );
}
